import React, {useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {fetchTaxAuthorityDepartments} from '@/entities/eployersControl/api';
import {
    $createEmployeeModal,
    $employeePending,
    doCreateEmployee,
    setCreateEmployeeModal,
} from '@/entities/eployersControl/model';
import {userModel} from '@/entities/user/model';
import {Button} from '@/shared/ui/Button';
import {Input} from '@/shared/ui/Input';
import {Modal} from '@/shared/ui/Modal';

import {AsyncSelect} from '@/shared/ui/Select';

import {CREATE_EMPLOYEE_FIELD_ROWS, CREATE_EMPLOYEE_FIELDS_I18N} from './fieldRows';
import {CreateEmployeeFormValues, getCreateEmployeeSchema} from './schema';
import {ButtonRow, Form, FormDescription, FormGrid, Row} from './styled';

const defaultValues: CreateEmployeeFormValues = {
    firstName: '',
    lastName: '',
    middleName: '',
    email: '',
    phone: '',
    taxAuthorityDepartment: null,
};

export const CreateEmployeeModal = () => {
    const createEmployeeModal = useStore($createEmployeeModal);
    const {t, i18n} = useTranslation();
    const employePending = useStore($employeePending);
    const isOperator = useStore(userModel.isOperator);
    const isChiefInspector = useStore(userModel.isChiefInspector);
    const isDirector = useStore(userModel.isDirector);
    const title = useMemo(() => {
        if (isDirector) return t('create_employee.createEmployee');
        if (isOperator) return t('create_employee.createChiefInspector');
        if (isChiefInspector) return t('create_employee.createInspector');
        return '';
    }, [isDirector, isChiefInspector, isOperator, t]);

    const translateValidates = useCallback(
        (key: string, replace?: Record<string, unknown>) =>
            t(`page_employers_control.create_employee.validations.${key}`, replace),
        [i18n.language, t],
    );

    const schema = useMemo(() => getCreateEmployeeSchema(translateValidates, isOperator), [
        isOperator,
        translateValidates,
    ]);

    const {control, handleSubmit, formState, errors, reset, setValue} = useForm<
        CreateEmployeeFormValues
    >({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues,
    });

    useEffect(() => {
        if (!createEmployeeModal) {
            reset(defaultValues);
        }
    }, [createEmployeeModal, reset]);

    useEffect(() => {
        if (createEmployeeModal && !isOperator) {
            setValue('taxAuthorityDepartment', null, {shouldValidate: true, shouldDirty: false});
        }
    }, [createEmployeeModal, isOperator, setValue]);

    const onSubmit = useCallback((data: CreateEmployeeFormValues) => {
        doCreateEmployee(data);
    }, []);

    return (
        <Modal
            show={createEmployeeModal}
            onClose={() => setCreateEmployeeModal(false)}
            title={title}
        >
            <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
                <FormDescription>
                    {t('page_employers_control.create_employee.description')}
                </FormDescription>
                <FormGrid>
                    {CREATE_EMPLOYEE_FIELD_ROWS.map((row, rowIndex) => (
                        <Row key={rowIndex}>
                            {row
                                .filter(
                                    (field) =>
                                        field.name !== 'taxAuthorityDepartment' || isOperator,
                                )
                                .map((field) => {
                                    const fieldError = errors[field.name];
                                    const isDirty = formState.dirtyFields[field.name];

                                    return (
                                        <Controller
                                            key={field.name}
                                            name={field.name}
                                            control={control}
                                            render={({onChange, value, name}) => {
                                                const hasValue =
                                                    String(value ?? '').trim().length > 0;
                                                if (field.name === 'taxAuthorityDepartment') {
                                                    return (
                                                        <AsyncSelect
                                                            disabled={employePending}
                                                            name={name}
                                                            value={value}
                                                            onChange={onChange}
                                                            label={t(
                                                                `${CREATE_EMPLOYEE_FIELDS_I18N}.${field.labelKey}`,
                                                            )}
                                                            defaultOptions
                                                            menuPlacement="bottom"
                                                            menuPosition="fixed"
                                                            loadOptions={async () => {
                                                                const response = await fetchTaxAuthorityDepartments();
                                                                return response.map((item) => ({
                                                                    label: item.name,
                                                                    value: item.id,
                                                                }));
                                                            }}
                                                            hasError={!!fieldError}
                                                            isRequired={field.isRequired}
                                                            isValid={
                                                                !!isDirty && !fieldError && hasValue
                                                            }
                                                            placeholder={t(
                                                                `page_employers_control.create_employee.tax_authority_department.placeholder`,
                                                            )}
                                                        />
                                                    );
                                                } else {
                                                    return (
                                                        <Input
                                                            disabled={employePending}
                                                            label={t(
                                                                `${CREATE_EMPLOYEE_FIELDS_I18N}.${field.labelKey}`,
                                                            )}
                                                            name={name}
                                                            formFieldName={name}
                                                            type={field.type}
                                                            value={value}
                                                            onChange={onChange}
                                                            hasError={!!fieldError}
                                                            isRequired={field.isRequired}
                                                            isValid={
                                                                !!isDirty && !fieldError && hasValue
                                                            }
                                                        />
                                                    );
                                                }
                                            }}
                                        />
                                    );
                                })}
                        </Row>
                    ))}
                </FormGrid>
                <ButtonRow>
                    <Button isLoading={employePending} type="submit" disabled={!formState.isValid}>
                        {t('page_employers_control.create_employee.submit')}
                    </Button>
                </ButtonRow>
            </Form>
        </Modal>
    );
};
